import React, { useRef, useState, useEffect } from "react";
import DetailPagetLayout from "./Layout";
import Photos from "./Photos";
import { JourneeType } from "data/types";
import type { ListingGalleryImage } from "components/ListingImageGallery/utils/types";
import Axios from '../../_services/caller.service';
import ModalBulkPhotoUpload from "components/ModalBulkPhotoUpload";
import { useTranslation } from "react-i18next";
import { ArrowUpTrayIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { voyageService } from "_services/voyage.service";

interface PhotosPageProps {
  eventCategoriesPage: JourneeType[];
  numDroit: number;
  idVoyage: string | number;
  setEventCategoriesPage: (categories: JourneeType[]) => void;
}

const PhotosPage: React.FC<PhotosPageProps> = ({ eventCategoriesPage, numDroit, idVoyage, setEventCategoriesPage }) => {
  const { t } = useTranslation();
  const canEditPhotos = Number(numDroit) === 1;

  const [images, setImages] = useState<ListingGalleryImage[] | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const updateDisplayMode = () => setIsMobile(mediaQuery.matches);
    updateDisplayMode();
    mediaQuery.addEventListener("change", updateDisplayMode);
    return () => mediaQuery.removeEventListener("change", updateDisplayMode);
  }, []);

  useEffect(() => {
    const objectUrls: string[] = [];
    const fetchImages = async () => {
      const imagePromises = eventCategoriesPage.flatMap((day, dayIndex) =>
        day.evenements.map(async (event, eventIndex) => {
          if (event.image && event.image !== "") {
            try {
              const response = await Axios.get(event.image, { responseType: 'blob' });
              const blob = new Blob([response.data], { type: 'image/jpeg' });
              const url = URL.createObjectURL(blob);
              objectUrls.push(url);

              let libelle="";

              if(event.typeEv === "N")
              {
                libelle="Nuit "+day.num_jour;
                if(event.libelle1 !== null && event.libelle1 !== "")
                {
                  libelle+=" · "+event.libelle1
                }
                if(event.libelle2 !== null && event.libelle2 !== "")
                {
                  if(event.libelle1 !== null && event.libelle1 !== "")
                  {
                    libelle+=" - "+event.libelle2
                  }
                  else
                  {
                    libelle+=" · "+event.libelle2
                  }
                }
              }
              else
              {
                libelle="Jour "+day.num_jour;
                if(event.libelle1 !== null && event.libelle1 !== "")
                {
                  libelle+=" · "+event.libelle1
                }
              }



               // Crée l'objet image
              const image = {
                url,
                libelle: libelle,
                stageKey: `${event.typeEv === "N" ? "nuit" : "lieu"}-${event.id}`,
                filename: event.image.split("/photo/")[1]?.split("?")[0],
              };

              return image; // Retourne l'image
            } catch (error) {
              console.error('Erreur lors du chargement de l\'image', error);
              return null;
            }
          }
          return null;
        })
      );

      const resolvedImages = await Promise.all(imagePromises);
      // Filtre les images nulles et attribue les ID
      const imagesWithId = resolvedImages
        .filter((image): image is { url: string; libelle: string; stageKey: string; filename: string } => image !== null && Boolean(image.filename))
        .map((image, index) => ({
          id: index, // Attribue l'ID en fonction de l'ordre dans le tableau
          ...image,
        }));

      setImages(imagesWithId);
    };

    fetchImages();

    // Nettoyage : Révoque les URL lorsque le composant est démonté
    return () => {
      objectUrls.forEach(URL.revokeObjectURL);
    };
  }, [eventCategoriesPage]);

  const handleUploaded = (updates: Array<{ stageKey: string; url: string }>) => {
    const urls = new Map(updates.map(update => [update.stageKey, update.url]));
    const updatedCategories = eventCategoriesPage.map(day => ({
      ...day,
      evenements: day.evenements.map(event => {
        const type = event.typeEv === "N" ? "nuit" : event.typeEv === "L" ? "lieu" : "";
        const url = type ? urls.get(`${type}-${event.id}`) : undefined;
        return url ? { ...event, image: `${url}?t=${Date.now()}` } : event;
      }),
    }));
    setEventCategoriesPage(updatedCategories);
    setSelectedFiles([]);
  };

  const openSelectedFiles = (files: FileList | null) => {
    if (files?.length) setSelectedFiles(Array.from(files));
  };

  const handleDeletePhoto = async (image: ListingGalleryImage) => {
    if (!canEditPhotos || !image.filename || !image.stageKey) return false;
    if (!window.confirm(t("photoBulk.deleteConfirmation", { label: image.libelle }))) return false;

    try {
      await voyageService.supprimerPhoto(idVoyage, image.filename);
      const updatedCategories = eventCategoriesPage.map(day => ({
        ...day,
        evenements: day.evenements.map(event => {
          const type = event.typeEv === "N" ? "nuit" : event.typeEv === "L" ? "lieu" : "";
          return `${type}-${event.id}` === image.stageKey ? { ...event, image: "" } : event;
        }),
      }));
      setEventCategoriesPage(updatedCategories);
      return true;
    } catch (error) {
      console.error("Unable to delete photo", error);
      alert(t("photoBulk.deleteError"));
      return false;
    }
  };

  return (
    <DetailPagetLayout images={images} onDeletePhoto={canEditPhotos ? handleDeletePhoto : undefined}>
      {canEditPhotos && (
        <div className="relative z-20 mt-8">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            className="hidden"
            onChange={event => {
              openSelectedFiles(event.target.files);
              event.target.value = "";
            }}
          />
          {isMobile ? <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF4A52] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#e83d45] focus:outline-none focus:ring-2 focus:ring-[#FF4A52] focus:ring-offset-2"
          >
            <PhotoIcon className="h-5 w-5" />
            {t("photoBulk.selectPhotos")}
          </button> : <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragEnter={event => {
              event.preventDefault();
              event.stopPropagation();
              setIsDragging(true);
            }}
            onDragOver={event => {
              event.preventDefault();
              event.stopPropagation();
              event.dataTransfer.dropEffect = "copy";
              setIsDragging(true);
            }}
            onDragLeave={event => {
              event.preventDefault();
              event.stopPropagation();
              const relatedTarget = event.relatedTarget;
              if (!(relatedTarget instanceof Node) || !event.currentTarget.contains(relatedTarget)) {
                setIsDragging(false);
              }
            }}
            onDrop={event => {
              event.preventDefault();
              event.stopPropagation();
              setIsDragging(false);
              openSelectedFiles(event.dataTransfer.files);
            }}
            className={`group relative flex min-h-[170px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed px-6 py-4 text-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF4A52] focus:ring-offset-2 dark:focus:ring-offset-neutral-900 ${
              isDragging
                ? "scale-[1.01] border-[#FF4A52] bg-[#FF4A52]/10 shadow-lg"
                : "border-neutral-300 bg-neutral-50 hover:border-[#FF4A52] hover:bg-[#FF4A52]/5 dark:border-neutral-700 dark:bg-neutral-800/50"
            }`}
          >
            <div className={`mb-3 flex h-8 w-8 items-center justify-center rounded-full transition-colors ${isDragging ? "bg-[#FF4A52] text-white" : "bg-white text-[#FF4A52] shadow-sm dark:bg-neutral-800"}`}>
              {isDragging ? <PhotoIcon className="h-7 w-7" /> : <ArrowUpTrayIcon className="h-5 w-5" />}
            </div>
            <div className="text-base font-semibold text-neutral-900 dark:text-white">
              {isDragging ? t("photoBulk.dropNow") : t("photoBulk.dropTitle")}
            </div>
           <div className="mt-3 rounded-full bg-[#FF4A52] px-5 py-2 text-sm font-medium text-white shadow-sm transition-transform group-hover:scale-105">
              {t("photoBulk.browse")}
            </div>
            <div className="mt-3 text-xs text-neutral-400">{t("photoBulk.formats")}</div>
          </div>}
        </div>
      )}
      <Photos images={images} numDroit={numDroit}/>
      {selectedFiles.length > 0 && (
        <ModalBulkPhotoUpload
          files={selectedFiles}
          idVoyage={idVoyage}
          eventCategories={eventCategoriesPage}
          onClose={() => setSelectedFiles([])}
          onUploaded={handleUploaded}
        />
      )}
    </DetailPagetLayout>
  );
};

export default PhotosPage;
